import { useExtraData } from "app/hooks";
import {
  doorCodeXL,
  doorCodeXLMin,
  doorCodeXXL,
  doorCodeXXLMin,
  useConfigurationOptionsContext,
} from "app/providers/DoorOptionsProvider";
import { SizeObject } from "app/providers/DoorOptionsProvider.types";
import Skeleton from "react-loading-skeleton";
import { OptionTemplate } from "shared/api/middleware-api-client";
import { defaultDoorWidth } from "shared/utils/doorSceneConstants";
import FourDoorNormalSize from "./components/fourDoor/FourDoorNormalSize";
import FourDoorXLSize from "./components/fourDoor/FourDoorXLSize";
import FourDoorXXLSize from "./components/fourDoor/FourDoorXXLSize";
import OneDoorNormalSize from "./components/oneDoor/OneDoorNormalSize";
import OneDoorXLSize from "./components/oneDoor/OneDoorXLSize";
import OneDoorXXLSize from "./components/oneDoor/OneDoorXXLSize";
import ThreeDoorNormalSize from "./components/threeDoor/ThreeDoorNormalSize";
import ThreeDoorXLSize from "./components/threeDoor/ThreeDoorXLSize";
import ThreeDoorXXLSize from "./components/threeDoor/ThreeDoorXXLSize";
import TwoDoorNormalSize from "./components/twoDoor/TwoDoorNormalSize";
import TwoDoorXLSize from "./components/twoDoor/TwoDoorXLSize";
import TwoDoorXXLSize from "./components/twoDoor/TwoDoorXXLSize";
import { WidthSelectorCard } from "./components/WidthSelectorCard";
import { WidthSelectorDescription } from "./components/WidthSelectorDescription";
import {
  FourDoorXLXXLCodes,
  OneDoorXLXXLCodes,
  ThreeDoorXLXXLCodes,
  TwoDoorXLXXLCodes,
} from "./XLXXLHelper";

const widthSizes = " gg-scale-[80%] sm:gg-scale-[100%]";

export const DoorWidthControls = () => {
  const {
    selectedDoor,
    color,
    glass,
    extra,
    eigenwijs,
    hasXLDoorOption,
    hasXXLDoorOption,
    updateExtra,
    updateEigenwijs,
    isExtraChecked,
    isSelfAssembly,
  } = useConfigurationOptionsContext();

  const { includedExtraOptions, isExtraError, isExtraDataLoading } =
    useExtraData(selectedDoor?.code, color, glass);

  // Doors
  const XLDoor = includedExtraOptions?.find((option) =>
    option.code.includes(doorCodeXL),
  );

  const XXLDoor = includedExtraOptions?.find((option) =>
    option.code.includes(doorCodeXXL),
  );

  // Prices
  const XLDoorPrice = `+€${XLDoor?.price},-`;

  const XXLDoorPrice = `+€${XXLDoor?.price},-`;

  const onExtraAdd = (item: OptionTemplate) => {
    if (isExtraChecked(extra, item)) return;
    let newExtraOptions = extra || [];
    // Add item
    newExtraOptions.push(item);

    // Scenario: if XL is already checked but the user clicks XXL -> deselect XL, select XXL
    if (hasXLDoorOption(newExtraOptions) && item.code.includes(doorCodeXXL)) {
      newExtraOptions = newExtraOptions.filter(
        (option) => !option.code.includes(doorCodeXL),
      );
      // Scenario: if XXL is already checked but the user clicks XL -> deselect XXL, select XL
    } else if (
      hasXXLDoorOption(newExtraOptions) &&
      item.code.includes(doorCodeXL)
    ) {
      newExtraOptions = newExtraOptions.filter(
        (option) => !option.code.includes(doorCodeXXL),
      );
    }

    updateExtra(newExtraOptions);

    // Handle XL and XXL door changes for extra options, only if the right extras were de/selected
    if (includesExtraLargeDoorCode(item)) {
      handleExtraXLXXLChange(newExtraOptions);
    }
  };

  // Handle XL and XXL door changes for extra options
  const handleExtraXLXXLChange = (newExtraOptions: OptionTemplate[]) => {
    // If no eigenwijs option is selected, return early
    if (!eigenwijs) return;
    // Get the extra option setups for XL - XXL doors
    const extraDoorCodeXL = hasXLDoorOption(newExtraOptions);
    const extraDoorCodeXXL = hasXXLDoorOption(newExtraOptions);

    // Create a new eigenwijs object
    const newEigenwijsOptions = { ...eigenwijs };

    // Calculate custom width based on selected extra options
    const customWidth = extraDoorCodeXL
      ? doorCodeXLMin
      : extraDoorCodeXXL
        ? doorCodeXXLMin
        : defaultDoorWidth;

    // Update custom width for each size object in eigenwijs options
    newEigenwijsOptions.sizeObjects = eigenwijs.sizeObjects.map(
      (sizeObject): SizeObject => ({
        ...sizeObject,
        customWidth: customWidth,
      }),
    );
    updateEigenwijs(newEigenwijsOptions);
  };
  if (!XLDoor || !XXLDoor) return null;

  // Check if the option includes XL or XXL door code
  const includesExtraLargeDoorCode = (item: OptionTemplate) => {
    return item.code.includes(doorCodeXL) || item.code.includes(doorCodeXXL);
  };

  const removeAllExtrasXLXXL = () => {
    if (!extra) return;

    const filteredExtras = extra.filter(
      (option) =>
        !option.code.includes(doorCodeXL) && !option.code.includes(doorCodeXXL),
    );
    updateExtra(filteredExtras);
    // Handle XL and XXL door changes for extra options, only if the right extras were de/selected
    handleExtraXLXXLChange(filteredExtras);
  };

  const isNormalActive =
    !isExtraChecked(extra, XLDoor) && !isExtraChecked(extra, XXLDoor);
  const isXLActive = isExtraChecked(extra, XLDoor);
  const isXXLActive = isExtraChecked(extra, XXLDoor);

  const doorCode = selectedDoor?.code ?? "";

  // Normal sized icon (1,2,3,4 doors)
  const normalIcon = OneDoorXLXXLCodes.includes(doorCode) ? (
    <OneDoorNormalSize isActive={isNormalActive} />
  ) : TwoDoorXLXXLCodes.includes(doorCode) ? (
    <TwoDoorNormalSize isActive={isNormalActive} />
  ) : ThreeDoorXLXXLCodes.includes(doorCode) ? (
    <ThreeDoorNormalSize isActive={isNormalActive} />
  ) : FourDoorXLXXLCodes.includes(doorCode) ? (
    <FourDoorNormalSize isActive={isNormalActive} />
  ) : (
    <OneDoorNormalSize isActive={isNormalActive} />
  );

  // XL sized icon (1,2,3,4 doors)
  const xlIcon = OneDoorXLXXLCodes.includes(doorCode) ? (
    <OneDoorXLSize isActive={isXLActive} />
  ) : TwoDoorXLXXLCodes.includes(doorCode) ? (
    <TwoDoorXLSize isActive={isXLActive} />
  ) : ThreeDoorXLXXLCodes.includes(doorCode) ? (
    <ThreeDoorXLSize isActive={isXLActive} />
  ) : FourDoorXLXXLCodes.includes(doorCode) ? (
    <FourDoorXLSize isActive={isXLActive} />
  ) : (
    <OneDoorXLSize isActive={isXLActive} />
  );

  // XXL sized icon (1,2,3,4 doors)
  const xxlIcon = OneDoorXLXXLCodes.includes(doorCode) ? (
    <OneDoorXXLSize isActive={isXXLActive} />
  ) : TwoDoorXLXXLCodes.includes(doorCode) ? (
    <TwoDoorXXLSize isActive={isXXLActive} />
  ) : ThreeDoorXLXXLCodes.includes(doorCode) ? (
    <ThreeDoorXXLSize isActive={isXXLActive} />
  ) : FourDoorXLXXLCodes.includes(doorCode) ? (
    <FourDoorXXLSize isActive={isXXLActive} />
  ) : (
    <OneDoorXXLSize isActive={isXXLActive} />
  );

  if (isExtraDataLoading || isExtraError) return <Skeleton height={200} />;
  return (
    <>
      <h5 className="gg-text-sm gg-font-bold gg-leading-[22.4px] gg-text-dark-blue">
        Breedte
      </h5>
      <div className="gg-grid gg-w-full gg-grid-cols-3 gg-justify-start gg-gap-5 gg-pb-1 gg-pt-2 sm:gg-flex sm:gg-flex-wrap sm:gg-items-start 2xl:gg-gap-[1.8rem]">
        {/* ITEM1 */}

        <WidthSelectorCard
          isActive={isNormalActive}
          onClick={removeAllExtrasXLXXL}
          disabled={isSelfAssembly}
          description={
            <WidthSelectorDescription title="NORMAAL" description="Tot 114cm" />
          }
        >
          <div className={widthSizes}>{normalIcon}</div>
        </WidthSelectorCard>

        {/* ITEM2 */}
        <WidthSelectorCard
          isActive={isXLActive}
          onClick={() => onExtraAdd(XLDoor)}
          disabled={isSelfAssembly}
          description={
            <WidthSelectorDescription
              title="XL"
              price={XLDoorPrice}
              description="Vanaf 115cm"
            />
          }
        >
          <div className={widthSizes}>{xlIcon}</div>
        </WidthSelectorCard>

        {/* ITEM3 */}

        <WidthSelectorCard
          isActive={isXXLActive}
          onClick={() => onExtraAdd(XXLDoor)}
          disabled={isSelfAssembly}
          description={
            <WidthSelectorDescription
              title="XXL"
              price={XXLDoorPrice}
              description="Tussen 145 en 175cm"
            />
          }
        >
          <div className={widthSizes}>{xxlIcon}</div>
        </WidthSelectorCard>
      </div>
    </>
  );
};
